import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { Prompt, ItsmChannel } from '../../../../types/backend';
import PromptSelector from '../../../../shared/components/PromptSelector';
import FreshserviceEntityPicker, {
  FreshserviceEntity,
} from '../../../../shared/components/FreshserviceEntityPicker';
import ChannelFieldMappings from './ChannelFieldMappings';
import { ITSM_CONTENT_KEYS } from '../../utils/ruleFormUtils';

type TicketPriority = NonNullable<ItsmChannel['priority']>;

/** Select value for an unset rule priority: the enrichment severity decides at dispatch. */
const FROM_SEVERITY = 'from_severity';

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

interface ItsmTargetCardProps {
  itsm: ItsmChannel | null;
  onChange: (itsm: ItsmChannel | null) => void;
  prompts: Prompt[];
  loadingPrompts: boolean;
  groups: FreshserviceEntity[];
  loadingGroups: boolean;
  agents: FreshserviceEntity[];
  loadingAgents: boolean;
  customerHasItsmConfig: boolean | null;
  /** Whether rule-level enrichment is on. Off: format prompt can't run; subject and description mappings are required. */
  summarize: boolean;
  hasAttemptedSave: boolean;
  validationErrors: Record<string, string>;
  sampleEvent: Record<string, unknown>;
  customerId: string;
  disabled?: boolean;
}

const ItsmTargetCard: React.FC<ItsmTargetCardProps> = ({
  itsm,
  onChange,
  prompts,
  loadingPrompts,
  groups,
  loadingGroups,
  agents,
  loadingAgents,
  customerHasItsmConfig,
  summarize,
  hasAttemptedSave,
  validationErrors,
  sampleEvent,
  customerId,
  disabled,
}) => {
  const enabled = itsm !== null;

  const toggle = (on: boolean) => {
    onChange(on ? { priority: 'medium', field_mappings: {} } : null);
  };

  const patch = (changes: Partial<ItsmChannel>) => {
    if (!itsm) return;
    onChange({ ...itsm, ...changes });
  };

  return (
    <Accordion
      variant="outlined"
      disableGutters
      sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={enabled ? <ExpandMoreIcon /> : null}
        sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => {
                e.stopPropagation();
                toggle(e.target.checked);
              }}
              size="small"
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
            />
          }
          label={<Typography variant="body2" fontWeight={600}>Create a support ticket</Typography>}
          onClick={(e) => e.stopPropagation()}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Opens a Freshservice ticket
        </Typography>
      </AccordionSummary>
      {enabled && itsm && (
        <AccordionDetails sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {customerHasItsmConfig === false && (
              <Typography variant="caption" color="warning.main">
                Freshservice not configured for this customer.
              </Typography>
            )}

            <FormControl fullWidth size="small" error={hasAttemptedSave && !!validationErrors.itsm_priority}>
              <InputLabel shrink>Ticket priority</InputLabel>
              <Select<TicketPriority | typeof FROM_SEVERITY>
                value={itsm.priority ?? FROM_SEVERITY}
                onChange={(e) => {
                  const value = e.target.value;
                  patch({ priority: value === FROM_SEVERITY ? undefined : value });
                }}
                label="Ticket priority"
                notched
                disabled={disabled}
              >
                <MenuItem value={FROM_SEVERITY} disabled={!summarize}>From severity</MenuItem>
                {PRIORITY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {(hasAttemptedSave && validationErrors.itsm_priority) ||
                  (itsm.priority ? 'Fixed for every ticket' : 'Warning: medium, error: high, critical: urgent')}
              </FormHelperText>
            </FormControl>

            <FreshserviceEntityPicker
              label="Group (optional)"
              options={groups}
              value={itsm.agent_group_id ?? null}
              onChange={(id) => patch({ agent_group_id: id ?? undefined })}
              loading={loadingGroups}
              disabled={disabled}
              helperText="Overrides the default group"
            />

            <FreshserviceEntityPicker
              label="Assign to (optional)"
              options={agents}
              value={itsm.responder_id ?? null}
              onChange={(id) => patch({ responder_id: id ?? undefined })}
              loading={loadingAgents}
              disabled={disabled}
              helperText="Overrides the default assignee"
            />

            {/* A format prompt reshapes the enrichment — it can't run with enrichment off. */}
            {summarize && (
              <PromptSelector
                prompts={prompts}
                value={itsm.format_prompt_id ?? null}
                onChange={(id) => patch({ format_prompt_id: id ?? undefined })}
                loading={loadingPrompts}
                label="Reword (optional)"
                placeholder="Default formatting"
              />
            )}

            <Box>
              <Typography variant="caption" color={summarize ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', mb: 1 }}>
                {summarize
                  ? 'Field mapping: optional, a filled field overrides AI'
                  : 'Field mapping: required, map both fields from the event'}
              </Typography>
              <ChannelFieldMappings
                keys={ITSM_CONTENT_KEYS}
                mappings={itsm.field_mappings || {}}
                onChange={(field_mappings) => patch({ field_mappings })}
                sampleEvent={sampleEvent}
                summarize={summarize}
                errors={hasAttemptedSave ? {
                  subject: validationErrors.itsm_mapping_subject,
                  description: validationErrors.itsm_mapping_description,
                } : {}}
                customerId={customerId}
                disabled={disabled}
              />
            </Box>
          </Box>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default ItsmTargetCard;
